<script>
  /*____________ Config  ____________*/
  export let tour;
  export let fromEmbed = false;
  export let embedId = null;
  
  /*____________ Imports ____________*/
  import { onDestroy } from "svelte";
  import i18n from "./i18n/i18n";
  import { Preloader } from "./preloader";
  const preloader = new Preloader(tour);
  import { tourFunctions } from "../tour-functions";
  const { convertColor } = tourFunctions;
  import { trackSceneViewStart } from "./analytics";

  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  /*____________ Components ____________*/
  import Krpano from "./Krpano.svelte";
  import Mapbox from "./Mapbox.svelte";
  import SwitchViews from "./SwitchViews.svelte";
  import MapButton from "./MapButton.svelte";
  import InfoSidebar from "./InfoSidebar.svelte";
  import Logo from "./Logo.svelte";
  import HamburgerMenu from "./HamburgerMenu.svelte";
  import NextScene from "./NextScene.svelte";
  import PrevScene from "./PrevScene.svelte";
  import WatermarkLogo from "./WatermarkLogo.svelte";
  import ThumbsGrid from "./ThumbsGrid.svelte";
  import SceneTitle from "./SceneTitle.svelte";
  import Video from "./Video.svelte";
  import SceneDescription from "./SceneDescription.svelte";
  import PopupHotspot from "./PopupHotspot.svelte";
  import PopupAccessibilityHotspot from "./PopupAccessibilityHotspot.svelte";
  import PopupSocial from "./PopupSocial.svelte";
  import SceneButton from "./SceneButton.svelte";
  import PopupGallery from "./PopupGallery.svelte";
  import CallToAction from "./CallToAction.svelte";
  import FormPopup from "./FormPopup.svelte";
  import Popup from "./Popup.svelte";
  import Ada from "./Ada.svelte";
  import Instructions from "./Instructions.svelte";
  import TextLogo from "./TextLogo.svelte";
  import PersonalizationButton from "./PersonalizationButton.svelte";
  import MobileControls from "./MobileControls.svelte";
  import Loader from "./Loader.svelte";
  import { trackHotspotClick } from "./analytics";
  import mapboxglSupported from '@mapbox/mapbox-gl-supported';
  
  const { callToAction, audioSettings, menu, theme, form } = tour;
  const embedAdjust = fromEmbed ? 60 : 0;   // px adjustment for close button on embed
  const sceneButton = tour.sceneButton;
  const colors = {
    themeColor: convertColor(theme?.primaryColor || "#000000"),
    controlsTextColor: convertColor(theme?.controlsTextColor || "#FFFFFF"),
    loaderColor: convertColor(theme?.loaderColor || "#FFFFFF"),
    loaderBg: convertColor(theme?.loaderBgColor || "#000000"),
    loaderTextColor: convertColor(theme?.loaderTextColor || "#FFFFFF"),
    hotspotHoverColor: convertColor(theme?.hotspotColor || "#000000"),
    calloutColor: convertColor(
      theme?.calloutColor || theme?.primaryColor || "#000000",
    ),
    hotspotColor: convertColor(theme?.hotspotColor || "#000000"),
    hotspotTextColor: convertColor(theme?.hotspotTextColor || "#FFFFFF"),
    popupTextColor: convertColor(theme?.popupTextColor || "#FFFFFF"),
    popupBgColor: convertColor(
      theme?.popupBgColor || theme?.primaryColor || "#FFFFFF",
    ),
    sceneButtonBgColor: convertColor(
      sceneButton?.bgColor || theme?.primaryColor || "#000000",
    ),
    sceneButtonTextColor: convertColor(sceneButton?.textColor || "#FFFFFF"),
    formPopupTextColor: convertColor(
      form?.popupTextColor || theme?.popupTextColor || "#FFFFFF",
    ),
    formPopupBgColor: convertColor(
      form?.popupBgColor ||
        theme?.popupBgColor ||
        theme?.primaryColor ||
        "#000000",
    ),
    focusColor: convertColor(theme?.focusColor || "#f7c60f"),
  };
  tourFunctions.setupColors(colors);
  const fonts = {
    fontHeading: tour.fontHeading || "Oswald",
    fontBody: tour.fontBody || "Raleway",
  };
  tourFunctions.setupFonts(fonts);

  /*____________ Variables ____________*/
  let liveFeedback;
  const listeners = [];
  let visibleUi = false;
  let mapVisible = false;
  let video;

  // Scene state management
  let sceneState = {
    allScenes: tour.scenes?.filter(scene => !scene.hidden && !scene.burried) || [],
    currentOrder: tour.scenes?.filter(scene => !scene.hidden && !scene.burried) || [],
    isPersonalizedTour: false
  };

  // Function to update scene order (called by ThumbsGrid)
  function updateSceneOrder(newOrder, isPersonalized = false) {
    sceneState.currentOrder = newOrder;
    sceneState.isPersonalizedTour = isPersonalized;
  }

  // Check if the user has webgl support
  const hasStaticSceneTitles = Boolean(tour.features?.includes("staticSceneTitles"));
  
  // ensure map is existing, enabled, & supported
  const hasMap =
    typeof tour.mapbox === "object" && 
    tour.features?.includes("map") && 
    tour.mapbox?.enabled && 
    mapboxglSupported.supported();

  if (tour.features?.includes("map") && !hasMap) {
    console.error("Map feature is enabled but no mapbox settings provided.");
  }

  // Track tour view if tracking feature is enabled
  const hasTracking = Boolean(tour.features?.includes("tracking"));
  if (hasTracking && tour.id) {
    trackTourView().catch(error => {
      console.error('Error tracking tour view:', error);
    });
  }

  let hasCurrentSceneAudio = false;
  const hasAnySceneAudio = tour.scenes.some((scene) => scene.audioId);
  let currentSceneName = null;
  const updateHasSceneAudio = (e) => {
    currentSceneName = e?.detail?.scene;
    if (!currentSceneName) return;
    const currentScene = tour.scenes.find((scene) => scene.name === currentSceneName);
    if (currentScene.audioId) {
      hasCurrentSceneAudio = true;
    } else {
      hasCurrentSceneAudio = false;
    }
  }
  addEventListener("krpano:newpano", updateHasSceneAudio);
  listeners.push({ type: "krpano:newpano", listener: updateHasSceneAudio });

  // Check for #/map hash and set initial expanded states
  const showMapFirst = window.location.hash === "#/map";
  let expandedMap = showMapFirst;
  let expandedTour = !showMapFirst;
  let mapLoading = showMapFirst; // Only load when starting with map

  let tourLogo = tour.logo;
  if (isMobile && tour.logoMobile) {
    tourLogo = tour.logoMobile;
  }

  const logoText = tour.logoText;
  if (!tourLogo?.url) {
    console.error("No logo provided");
    tourLogo = {
      url: null,
      width: 150,
      height: 60,
      text: logoText ||  "Logo",
    };
  }

  const mapLogo = tour.mapLogo || tourLogo;
  // mobile: loading mobile -> loading -> tour logo mobile -> tour logo
  // desktop: loading -> tour logo
  const logoLoading =
    (isMobile && tour.logoLoadingMobile?.url
      ? tour.logoLoadingMobile
      : tour.logoLoading) || tourLogo;
  const tourTitle = tourFunctions.parseContent(tour.name);
  const sceneButtonSettings = tour.sceneButton ? tour.sceneButton : {};
  // audio
  let sceneAudioPlaying;
  // Map variables
  let zoom = 14;
  const setMapZoom = () => {
    if (isMobile && tour?.mapbox?.mobile) {
      // For mobile devices with mobile mapbox settings
      const mobileSettings = tour.mapbox.mobile;
      const defaultSettings = tour.mapbox;
      
      zoom = expandedMap
        ? mobileSettings?.zoomExpandedMap ?? defaultSettings.zoomExpandedMap
        : mobileSettings?.zoomMiniMap ?? defaultSettings.zoomMiniMap;
    } else {
      // For desktop or no mobile settings
      const { zoomExpandedMap, zoomMiniMap } = tour.mapbox;
      zoom = expandedMap ? zoomExpandedMap : zoomMiniMap;
    }
    return zoom;
  };

  const visibleScenes = tour.scenes.filter((scene) => !scene.hidden);

  const getDimension = (autoSize, defaultValue, desktopValue, mobileValue) => {
    // is autoSize enabled?
    if (autoSize) return defaultValue;
    // was a desktop value provided?
    if (!isMobile && desktopValue) return desktopValue;
    // was a mobile value provided?
    if (isMobile && mobileValue) return mobileValue;
    // no values provided, return default
    return defaultValue;
  };

  let sceneButtonLabel = sceneButtonSettings?.label ?? $i18n.t('tour.sceneButton');
  
  // scene button width
  const defaultWidth = tourLogo.width * 0.8;
  const sceneButtonWidth = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultWidth,
    sceneButtonSettings.width,
    sceneButtonSettings.mobileWidth,
  );

  // scene button x position
  const defaultX = tourLogo.width / 2 - sceneButtonWidth / 2 + 20;
  let sceneButtonX = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultX,
    sceneButtonSettings.x,
    sceneButtonSettings.mobileX,
  );
  sceneButtonX = !tourLogo.url && logoText ? 20 : sceneButtonX;

  // scene button y position
  const defaultY = tourLogo.height + 25;
  const sceneButtonY = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultY,
    sceneButtonSettings.y,
    sceneButtonSettings.mobileY,
  );

  document.addEventListener("focusin", (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("focusin", e.target);
    }
  });

  // Function to trigger keydown event on the krpano element
  function sendKeyToKrpano(keyCode, keyName) {
    // ensure a popup is not open
    const popup = document.querySelector(".popup");
    const thumbs = document.querySelector(".thumbs-container");
    const isThumbsOpen = thumbs && thumbs.classList.contains("active");
    if (popup || isThumbsOpen) return;

    const krpanoElem = document.querySelector("#krpanoSWFObject");
    if (!krpanoElem) {
      console.error("krpano element not found.");
      return;
    }

    // Set focus to krpano
    if (document.activeElement !== krpanoElem) {
      krpanoElem.focus();
    }

    // Create a new keyboard event for keydown
    const keydownEvent = new KeyboardEvent("keydown", {
      key: keyName,
      code: keyName,
      keyCode: keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true,
    });

    keydownEvent.isSynthetic = true; // Mark the event as synthetic to prevent infinite loops

    // Dispatch the keydown event to the krpano element
    krpanoElem.dispatchEvent(keydownEvent);
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log(`Keydown event for "${keyName}" dispatched to krpano.`);
    }
  }

  // Stop AutoNav on user interaction
  document.addEventListener("mousedown", tourFunctions.stopAutoNav);
  listeners.push({ type: "mousedown", listener: tourFunctions.stopAutoNav });
  
  document.addEventListener("touchstart", tourFunctions.stopAutoNav);
  listeners.push({ type: "touchstart", listener: tourFunctions.stopAutoNav });

  const keydownListener = function (event) {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("Keydown event", event);
    }
    // Ignore synthetic events to prevent infinite loops
    if (event.isSynthetic) return;

    // if the tour is not expanded, don't allow any keydown events
    if (!expandedTour) return;

    // Check for popup and ignore hotkeys if it's open
    const hasPopup = document.querySelector(".popup");
    const hasThumbs = document.querySelector(".thumbs-container.active");
    if (hasPopup || hasThumbs) return;

    switch (event.key) {
      // Hide/Show UI
      case "H":
          visibleUi = !visibleUi;
          if (visibleUi) {
            tourFunctions.showHotspots();
            liveFeedback.textContent = $i18n.t('tour.hotkeyInterfaceHide');
          } else {
            tourFunctions.hideHotspots();
            liveFeedback.textContent = $i18n.t('tour.hotkeyInterfaceShow');
          }
        break;
      // Start / Stop AutoNav
      case "A":
          const autoNavStatus = tourFunctions.getAutoNavStatus();
          if (!autoNavStatus) {
            // autoNav will be enabled
            const autoNavInterval = prompt($i18n.t('tour.autoNavInterval'), "10");
            tourFunctions.stopAutoNav();
            tourFunctions.loadSceneByIndex(0, 'auto_nav');
            setTimeout(() => {
              tourFunctions.startAutoNav(autoNavInterval);
            }, 1000);
          } else {
            // autoNav will be disabled
            tourFunctions.stopAutoNav();
          }
          liveFeedback.textContent = $i18n.t('tour.hotkeyAutoNav');
        break;
      // Take a Screenshot
      case "S":
        // prompt for width and height
        const width = prompt($i18n.t('tour.screenshotWidth'), window.innerWidth);
        const height = prompt($i18n.t('tour.screenshotHeight'), window.innerHeight);
        const screenshot = tourFunctions.currentScene.screenshot(width, height, false, 'png', 1.0);
        // save the screenshot
        if (screenshot) {
          const filenamize = (str) => str.replace(/\s/g, "-").toLowerCase();
          const sceneTitle = tourFunctions.currentScene.getTitle();
          const filename = filenamize(tour.name + "-" + sceneTitle) + ".png";
          const downloadLink = document.createElement("a");
          downloadLink.href = screenshot;
          downloadLink.download = filename;
          downloadLink.click();
        }

        break;
      // Show Instructions
      case "i":
        toggleInstructions();
        if (instructionsVisible) {
          liveFeedback.textContent = $i18n.t('tour.hotkeyShowInstructions');
        } else {
          liveFeedback.textContent = $i18n.t('tour.hotkeyHideInstructions');
        }
        break;
      // Refocus the krpano element
      case "Escape":
        // check if a popup is open
        const popup = document.querySelector(".popup");
        if (popup) return;
        
        // If we're in an embed context (iframe), handle embed close
        if (fromEmbed || window.parent !== window) {
          if (embedId) {
            window.parent.postMessage({
              type: 'covecreek:close',
              embedId: embedId
            }, '*');
          }
        } else {
          // Only handle escape for non-embed contexts
          tourFunctions.focus();
          liveFeedback.textContent = $i18n.t('tour.hotkeyEscape');
        }
        break;
      // Navigate the scene
      case "ArrowLeft":
        sendKeyToKrpano(37, "ArrowLeft");
        break;
      case "ArrowRight":
        sendKeyToKrpano(39, "ArrowRight");
        break;
      case "ArrowUp":
        sendKeyToKrpano(38, "ArrowUp");
        break;
      case "ArrowDown":
        sendKeyToKrpano(40, "ArrowDown");
        break;
      // Next/Previous Scene
      case ",":
        tourFunctions.prevScene();
        break;
      case ".":
        tourFunctions.nextScene('keyboard');
        break;
      // Zoom in/out
      case "-":
        sendKeyToKrpano(189, "-");
        break;
      case "=":
        sendKeyToKrpano(187, "=");
        break;
    }
  };
  document.addEventListener("keydown", keydownListener);
  listeners.push({ type: "keydown", listener: keydownListener });

  /*____________ Functions ____________*/
  function showUi() {
    visibleUi = true;
    // Show map when UI becomes visible
    mapVisible = true;
  }

  // gets the hotspot content from the krpano xml
  function getHotspotContent(hotspot) {
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.currentScene.getHotspot(hotspot);
      if (!hotspot) return;
    }

    let gallery = null;
    if (hotspot.popup_gallery) {
      gallery = tourFunctions.currentScene.getGallery(hotspot.popup_gallery);
    }

    const {
      id,
      name,
      popup_title,
      popup_text,
      popup_image,
      popup_image_alt,
      popup_video_embed,
      popup_directions,
    } = hotspot;
    const title = popup_title?.replace(/\[.*?\]/g, " ");
    const content = popup_text ? tourFunctions.parseContent(popup_text) : "";

    // parse string to directions array with type
    const parseDirections = (str) => {
      if (!str) return [];
      
      // Split by pipe to get individual direction entries
      const directionEntries = str.split("|").map(entry => entry.trim()).filter(entry => entry);
      
      return directionEntries.map(entry => {
        const parts = entry.split(",").map(part => part.trim());
        // Each entry should have lat, long, type
        if (parts.length !== 3) return null;
        
        const [lat, long, type] = parts;
        return {
          lat: parseFloat(lat),
          long: parseFloat(long),
          type: type // 'parking' or 'building'
        };
      }).filter(entry => entry !== null); // Remove any invalid entries
    }
    
    const directions = parseDirections(popup_directions);

    return {
      id,
      name,
      title,
      image: popup_image,
      imageAlt: popup_image_alt,
      content,
      videoUrl: popup_video_embed,
      gallery,
      directions,
      handleVideoLoad: function () {
        video = popup_video_embed;
        sceneAudioPlaying = false;
        // loadVideo(hotspot.popup_video_embed);
      },
    };
  }


  function openHotspotInfo(hotspot) {
    // if hotspotName was provided instead of {object}
    if (typeof hotspot === "string") {
      let hotspotName = hotspot;
      hotspot = getHotspotContent(hotspotName);
    }
    if (!hotspot) return;

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    new PopupHotspot({
      target: document.getElementById("popup-container"),
      props: { ...hotspot },
    });
  }

  function openAccessibilityInfo(hotspot) {
    // if hotspotName was provided instead of {object}
    if (typeof hotspot === "string") {
      let hotspotName = hotspot;
      hotspot = getHotspotContent(hotspotName);
    }
    if (!hotspot) return;

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    new PopupAccessibilityHotspot({
      target: document.getElementById("popup-container"),
      props: { ...hotspot },
    });
  }

  let gallery;
  let popupSocial = null;

  function openGalleryInfo(hotspot) {
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.currentScene.getHotspot(hotspot);
    }
    if (!hotspot) return;
    console.log('openGalleryInfo', hotspot);

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    gallery = new PopupGallery({
      target: document.getElementById("popup-container"),
      props: {
        name: hotspot.name,
        title: tourFunctions.parseContent(hotspot.popup_title),
        gallery: tourFunctions.currentScene.getGallery(hotspot.popup_gallery),
      },
    });
  }

  /*____________ Krpano Events ____________*/
  const krpanoLittlePanetEndHandler = (e) => {
    showUi();
    preloader.preloadCurrentScene();
  };
  addEventListener("krpano:littleplanetend", krpanoLittlePanetEndHandler);
  listeners.push({ type: "krpano:littleplanetend", listener: krpanoLittlePanetEndHandler });

  function trackHotspotEvent(hotspot, type) {
    const hs = typeof hotspot === 'string' ? 
      tourFunctions.currentScene.getHotspot(hotspot) : hotspot;
      
    if (!hs) return;

    // Handle special case for tooltips style
    const hotspotType = hs.style === 'skin_tooltips' ? 
      `hotspot_${type}` : hs.style;

    const trackHotspot = {
      hotspot_id: hs.id,
      hotspot_title: hs.tooltip ?? hs.title,
      hotspot_type: hotspotType
    };

    trackHotspotClick(trackHotspot);
  }

  const krpanoInfoClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:infoclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openHotspotInfo(e.detail.hotspot);
    trackHotspotEvent(hs, 'info');
  };
  addEventListener("krpano:infoclick", krpanoInfoClickHandler);
  listeners.push({ type: "krpano:infoclick", listener: krpanoInfoClickHandler });

  const krpanoAccessibilityClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:accessibilityclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openAccessibilityInfo(e.detail.hotspot);
    trackHotspotEvent(hs, 'accessibility');
  };
  addEventListener("krpano:accessibilityclick", krpanoAccessibilityClickHandler);
  listeners.push({ type: "krpano:accessibilityclick", listener: krpanoAccessibilityClickHandler });

  const krpanoGalleryClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:galleryclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openGalleryInfo(hs);
    trackHotspotEvent(hs, 'gallery');
  };
  addEventListener("krpano:galleryclick", krpanoGalleryClickHandler);
  listeners.push({ type: "krpano:galleryclick", listener: krpanoGalleryClickHandler });

  function closeSocial() {
    popupSocial = false;
    sceneAudioPlaying = true;
    tourFunctions.focus();
  }

  const krpanoSocialClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:socialclick", e);
    }
    // Pause scene audio when social popup opens (same as video behavior)
    sceneAudioPlaying = false;
    
    // get the hotspot from the krpano
    const scene = tour.scenes.find(s => s.id === e.detail.scene_id);
    const hotspot = scene.hotspots.find(h => h.id === e.detail.hotspot_id);
    // set the social popup
    const url = hotspot.popupSocialUrl;
    const platform = 'instagram';
    const title = hotspot.title;
    const content = hotspot.popupSocialContent;
    const hasDescription = hotspot.popupSocialHasDescription ?? false;
    
    // set and open the social popup 
    popupSocial = { url, platform, title, content, hasDescription }
    // track the hotspot event in the analytics
    trackHotspotEvent(hotspot, 'social');
  };
  addEventListener("krpano:socialclick", krpanoSocialClickHandler);
  listeners.push({ type: "krpano:socialclick", listener: krpanoSocialClickHandler });

  const krpanoLinkClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:linkclick", e);
    }
    const { hotspot, link, hotspotId, sceneId } = e.detail;
    // Open link in new tab
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      // Track the hotspot event in the analytics
      const hs = tourFunctions.currentScene.getHotspot(hotspot);
      trackHotspotEvent(hs, 'link');
    }
  };
  addEventListener("krpano:linkclick", krpanoLinkClickHandler);
  listeners.push({ type: "krpano:linkclick", listener: krpanoLinkClickHandler });

  let menuActive;
  let adaVisible = false;
  let instructionsVisible = false;
  let mapLocationsVisible = false;
  let hasExecutedQuerystring = false;
  let hasAdaParam;
  let mapComponent;
  // Handle Popup Ada & Instructions
  $: if (visibleUi && !hasExecutedQuerystring) {
    // ?ada query string
    const urlParams = new URLSearchParams(window.location.search);
    const adaSlug = tour.accessibilitySlug || "ada";
    const adaParam = urlParams.get(adaSlug);
    hasAdaParam = adaParam === "true" || adaParam === "";
    if (hasAdaParam) adaVisible = true;
    hasExecutedQuerystring = true; // Set the flag to true after execution
  }

  // Hash navigation for thumbs grid
  let thumbsGridComponent; // Reference to ThumbsGrid component
  const isHashPersonalization = window.location.hash === "#/personalization";
  const isHashCategory = window.location.hash.match(/^#\/category\/(\d+)$/);
  const isHashCategoryAll = window.location.hash === "#/category/all";
  const isHashThumbGrid = isHashPersonalization || isHashCategory || isHashCategoryAll;
  
  $: if (isHashThumbGrid && visibleUi && thumbsGridComponent) {
    thumbsGridComponent.openThumbs();
    if (isHashPersonalization && tour.features?.includes("tags")) {
      thumbsGridComponent.activatePersonalization();
    } else if (isHashCategory && tour.features?.includes("categories")) {
      const categoryMatch = window.location.hash.match(/^#\/category\/(\d+)$/);
      const categoryIndex = categoryMatch ? parseInt(categoryMatch[1]) : null;
      if (isHashCategoryAll ||categoryIndex === null) {
        thumbsGridComponent.activateCategoryAll();
      } else {
        thumbsGridComponent.activateCategory(categoryIndex);
      }
    }
  }

  // Handle hotspot hash routing
  function handleHotspotHashLoaded({ hotspotId }) {
    if (!hotspotId) return;
    let hs = tourFunctions.currentScene.getHotspotById(hotspotId);
    // determine what type of hotspot it is
    if (hs.style === 'hotspot_gallery' || hs.style === 'hotspot_inline_photo_gallery') {
      openGalleryInfo(hs);
    } else if (hs.style === 'hotspot_social') {
      // do nothing
    } else if (hs.style === 'hotspot_accessibility') {
      hs = getHotspotContent(hs.name);
      openAccessibilityInfo(hs);
    } else if (hs.style === 'hotspot_info') {
      hs = getHotspotContent(hs.name);
      openHotspotInfo(hs);
    } else {
      hs = getHotspotContent(hs.name);
      openHotspotInfo(hs);
    }
  }



  function handleHamburgerAda() {
    adaVisible = true;
    menuActive = false;
  }

  function handleHamburgerInstructions() {
    instructionsVisible = true;
    menuActive = false;
  }

  function handleVr() {
    menuActive = false;
    // Create a new URL
    const url = new URL(window.location.href);
    url.search = ''; // remove existing query string
    url.hash = ''; // clear #hash links
    url.searchParams.set('vr', true);
    // Load the new URL
    window.location.href = url.toString();
  }

  function toggleInstructions() {
    instructionsVisible = !instructionsVisible;
  }

  function closeAda() {
    adaVisible = false;
    tourFunctions.focus();
  }

  function closeInstructions() {
    instructionsVisible = false;
    tourFunctions.focus();
  }

  function handlePersonalize() {
    if (thumbsGridComponent) {
      thumbsGridComponent.openThumbs();
      thumbsGridComponent.activatePersonalization();
    }
  }

  // Open Videos
  function closeVideo() {
    sceneAudioPlaying = true;
    tourFunctions.focus();
  }

  function handleVideoLoadEvent(e) {
    video = e.detail.video;
    sceneAudioPlaying = false;
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log('krpano:videoclick', e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    trackHotspotEvent(hs, 'video');
  }

  addEventListener("krpano:videoclick", handleVideoLoadEvent);
  listeners.push({ type: "krpano:videoclick", listener: handleVideoLoadEvent });

  // Scene logos
  // if(tour.sceneLogos){
  //     addEventListener('krpano:newpano', (e)=>{
  //         let sceneLogo = tourFunctions.currentScene.getLogo();
  //         tourLogo.url = sceneLogo ? sceneLogo : tour.logo.url;
  //     });
  // }

  function switchView() {
    expandedTour = !expandedTour;
    expandedMap = !expandedMap;
    expandedTour ? tourFunctions.showHotspots() : tourFunctions.hideHotspots();
    setMap();
    
    // Update URL hash
    if (expandedMap) {
      window.location.hash = "/map" 
    } else {
      tourFunctions.setStopUrl();
    }

    // Track Map View / Scene View
    if (expandedMap) {
      trackSceneViewStart({
        scene_id: 'map',
        scene_title: 'map',
        scene_source: 'map_button',
      });
    } else {
      const scene = tourFunctions.currentScene.getScene();
      const scene_id = scene.id;
      const scene_title = scene.title;
      trackSceneViewStart({
        scene_id,
        scene_title,
        scene_source: 'map_button',
      });
    }
    // Clear the pending source after using it
    window.CC.pendingSceneSource = null;

    
    // Reset map center when switching to small view
    if (!expandedMap && mapComponent) {
      // if in dev mode, log the event
      if (process.env.NODE_ENV === 'development') {
        console.log("resetting map center");
      }
      const map = mapComponent.getMap();
      const mapOptions = isMobile && tour?.mapbox?.mobile ? {
        center: tour?.mapbox?.mobile?.mapCenter ?? tour?.mapbox?.mapCenter,
        zoom: tour?.mapbox?.mobile?.zoomMiniMap ?? tour?.mapbox?.zoomMiniMap,
        pitch: tour?.mapbox?.mobile?.mapPitch ?? tour?.mapbox?.mapPitch,
        bearing: tour?.mapbox?.mobile?.mapBearing ?? tour?.mapbox?.mapBearing
      } : {
        center: tour?.mapbox?.mapCenter,
        zoom: tour?.mapbox?.zoomMiniMap,
        pitch: tour?.mapbox?.mapPitch,
        bearing: tour?.mapbox?.mapBearing
      };
      map.jumpTo(mapOptions);
    }
    // Reset focus to the accessibility tour button
    document.getElementById('accessibility-tour-button')?.focus();

    // Remove any existing focus
    if (document.activeElement) {
      document.activeElement.blur();
    }

    

  }

  function setMap() {
    window.CC = window.CC || {};
    window.CC.switchView = switchView;
    setMapZoom();
  }
  if (hasMap) {
    setMap();
  }

  // Track tour view if tracking feature is enabled
  async function trackTourView() {
    if (!hasTracking || !tour.id) return;
    
    try {
      const response = await fetch('https://api.vntpoint.com/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tourId: tour.id
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('Tour view tracked successfully:', result);
        }
      } else {
        console.error('Failed to track tour view:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error tracking tour view:', error);
    }
  }

  onDestroy(() => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("Tour destroyed");
    }
    gallery?.$destroy();
    listeners.forEach(({ type, listener }) => {
      removeEventListener(type, listener);
    });
  });
</script>

<!-- /*____________ Markup ____________*/ -->
{#if form && form.enabled && !hasAdaParam}
  <FormPopup {...form} />
{/if}
<div id="popup-container"></div>
<Popup
  visible={adaVisible}
  on:close={closeAda}
  ariaLabel={$i18n.t('tour.accessibilityTour')}
  width={"100%"}
>
  <Ada {tour} />
</Popup>

<Popup visible={instructionsVisible} on:close={closeInstructions}>
  <Instructions />
</Popup>

{#if popupSocial && popupSocial.url && popupSocial.platform}
  <PopupSocial on:close={closeSocial} {...popupSocial} />
{/if}

<!-- Map Loader - shows when accessing via #/map URL -->
{#if mapLoading && expandedMap}
  <Loader
    logo={logoLoading.url}
    loading={false}
    delay="3000"
    width={logoLoading.width}
    tour={tour}
    callback={() => { mapLoading = false; mapVisible = true;}}
  />
{/if}

{#if visibleUi}
   <!-- {#if } -->
  <!-- Accessibility button that becomes visible during tabbing -->
  <button
  id="accessibility-tour-button"
  tabindex="0"
  on:click={() => {
    if (expandedMap) {
      mapLocationsVisible = !mapLocationsVisible;
    } else {
      adaVisible = true;
    }
  }}
  >
  {expandedMap ? (mapLocationsVisible ? 'Close Map Locations' : 'Open Map Locations') : 'Open Accessibility Tour'}
  </button>

  {#if tour.infoSidebar === true}
    <InfoSidebar on:loadvideo={handleVideoLoadEvent} logo={tourLogo} />
  {/if}
  <!-- has map -->
  {#if hasMap && !isMobile}
    <MapButton click={switchView} {expandedMap} bgColor={theme?.primaryColor} />
  {/if}

  <Video bind:src={video} onClose={closeVideo} />

  <!-- no sidebar -->
  {#if !tour.infoSidebar}

    {#if tourLogo.url}
    <Logo
      linkto={tour.logoUrl || null}
      image={expandedMap ? mapLogo.url : tourLogo.url}
      width={expandedMap ? mapLogo.width : tourLogo.width}
      opacity="0.85"
    />
    {:else}
    <TextLogo text={logoText || "Logo"} />
    {/if}

    {#if sceneButtonSettings?.enabled !== false}
      <SceneButton
        text={sceneButtonLabel}
        x={sceneButtonX}
        y={sceneButtonY}
        width={sceneButtonWidth}
        opacity={0.8}
        scene={visibleScenes?.[0]}
      />
    {/if}
  {/if}

  <div class="top-left" style={`right: ${embedAdjust}px; left: auto;`}>
    {#if callToAction}
      <CallToAction {callToAction} />
    {/if}

    {#if menu}
      <HamburgerMenu
        menus={menu}
        {tour}
        bind:active={menuActive}
        on:ada={handleHamburgerAda}
        on:instructions={handleHamburgerInstructions}
        on:vr={handleVr}
      />
    {/if}
  </div>

  {#if !tour.features?.includes("whitelabel")}
    <WatermarkLogo />
  {/if}

  {#if expandedTour}
    {#if !isMobile && tour.scenes.length > 1}
      <PrevScene {tour} {sceneState} />
      <NextScene {tour} {sceneState} />
    {/if}
    <SceneTitle isStatic={hasStaticSceneTitles} />
    <ThumbsGrid
      title={tourTitle}
      showPrevNext={false}
      bind:this={thumbsGridComponent}
      {tour}
      {sceneState}
      {updateSceneOrder}
    />
  {/if}

  <!-- Desktop Scene Description -->
  {#if hasAnySceneAudio || tour.features?.includes('sceneDescriptions')}
    <SceneDescription
      {tour}
      visible={expandedTour}
      playing={sceneAudioPlaying}
      loop={audioSettings?.looping || false}
      resumable={audioSettings?.resumable || true}
      enableSceneAudio={hasAnySceneAudio && tour.features?.includes('audio')}
      enableDescriptionAudio={tour.features?.includes('sceneDescriptions')}
      hasPersonalizationButton={tour.features?.includes('personalizationButton')}
      onPersonalize={handlePersonalize}
      onPlayingChange={(isPlaying) => sceneAudioPlaying = isPlaying}
    />
  {/if}

  <!-- Mobile Controls - handles all mobile button positioning and description -->
  <MobileControls
    hasPersonalization={tour.features?.includes('personalizationButton')}
    hasDescription={tour.features?.includes('sceneDescriptions')}
    hasAudio={hasAnySceneAudio && tour.features?.includes('audio')}
    hasMap={hasMap}
    {expandedMap}
    {tour}
    playing={sceneAudioPlaying}
    loop={audioSettings?.looping || false}
    resumable={audioSettings?.resumable || true}
    enableSceneAudio={hasAnySceneAudio && tour.features?.includes('audio')}
    enableDescriptionAudio={tour.features?.includes('sceneDescriptions')}
    onPlayingChange={(isPlaying) => sceneAudioPlaying = isPlaying}
    {theme}
    on:personalize={handlePersonalize}
    on:mapClick={switchView}
  />
{/if}

{#if !hasMap}
  <Krpano
    config={tour}
    {colors}
    expanded={expandedTour}
    xml="tour.xml"
    logoLoading={logoLoading.url}
    logoLoadingWidth={logoLoading.width}
    onHotspotHashLoaded={handleHotspotHashLoaded}
    loaderEnabled={!showMapFirst}
  />
{/if}

<!-- Has Map -->
{#if hasMap}
  <div
    class:has-audio={hasAnySceneAudio}
    class={expandedTour ? "large-view" : "small-view"}
    class:loading={mapLoading}
  >
    <button
      class="hide-large view-switch-button"
      on:click={switchView}
      aria-label={$i18n.t('tour.showTour')}
      on:keydown={(e) => e.key === 'Enter' && switchView()}
    >
      <div class="switch-hover">{$i18n.t('tour.showTour')}</div>
      <SwitchViews />
    </button>
    <div inert={!expandedTour}>
      <Krpano
        config={tour}
        {colors}
        expanded={expandedTour}
        xml="tour.xml"
        logoLoading={logoLoading.url}
        logoLoadingWidth={logoLoading.width}
        onHotspotHashLoaded={handleHotspotHashLoaded}
        loaderEnabled={!showMapFirst}
      />
    </div>
  </div>

  {#if mapVisible}
    <div
      class:has-audio={hasAnySceneAudio}
      class={expandedMap ? "large-view" : "small-view"}
    >
      <button
        class="hide-large view-switch-button"
        on:click={switchView}
        aria-label={$i18n.t('tour.showMap')}
        on:keydown={(e) => e.key === 'Enter' && switchView()}
      >
        <div class="switch-hover">{$i18n.t('tour.showMap')}</div>
        <SwitchViews />
      </button>
      <div inert={!expandedMap}>
        <Mapbox 
          {tour} 
          expanded={expandedMap} 
          {zoom} 
          bind:mapComponent 
          bind:mapLocationsVisible
        />
      </div>
    </div>
  {/if}
{/if}
<!-- END Has Map -->

<div
  aria-live="assertive"
  id="liveFeedback"
  bind:this={liveFeedback}
  style="position: absolute; left: -9999px; top: -9999px;"
></div>

<!-- /*____________ CSS ____________*/ -->
<style>
  #accessibility-tour-button {
  position: absolute;
  top: -9999px;
  left: 0;
  width: 100%;
  padding: 0.5rem;
  border: none;
  background: #f7c60f;
  color: #001eff;
  font-size: 1rem;
  cursor: pointer;
  z-index: 9999;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  opacity: 0;
  font-weight: 700;
}

#accessibility-tour-button:focus {
  opacity: 1;
  top: 0;
}


  :global(.clicktext) {
    font-weight: 900;
    font-size: 14px;
    text-decoration: underline;
  }

  .top-left {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;
    width: 100%;
  }

  .close-embed {
    position: fixed;
    top: 0;
    right: 0;
    padding: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    cursor: pointer;
    z-index: 999;
  }

  .view-switch-button {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    z-index: 10;
  }

  .view-switch-button:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
  }

  .switch-hover {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    position: absolute;
    left: 0;
    top: 0;
    z-index: 9;
    color: #fff;
    display: none;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;

    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .small-view:hover .switch-hover,
  .small-view .view-switch-button:focus .switch-hover {
    display: flex;
  }

  .small-view {
    display: block;
    width: 300px;
    height: 200px;

    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 800;

    margin: 20px;

    border: 3px solid white;
    border-radius: 10px;
    box-shadow: -3px 3px 10px #000;
    background: white;
  }

  .small-view.loading {
    opacity: 0;
    visibility: hidden;
  }

  .small-view.has-audio {
    bottom: 40px;
  }

  .large-view {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  .large-view .hide-large {
    display: none;
  }

  :global(.large-view .mapboxgl-map) {
    height: 100vh;
    width: 100vw;
  }

  :global(.small-view .mapboxgl-map) {
    height: 200px;
    width: 300px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }

  :global(*:focus-visible) {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
    border-radius: 0 !important;
  }

  @media only screen and (max-width: 600px) {
    .small-view {
      display: none;
    }
  }


</style>
